/**
 * Generic date / duration formatters used across the dashboard.
 */

export const formatDuration = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) {
    return '—';
  }

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (mins < 60) {
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }

  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;

  return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
};

export const formatTimestamp = (iso?: string | null): string => {
  if (!iso) {
    return '—';
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return date.toLocaleString();
};

export const formatTimestampShort = (iso?: string | null): string => {
  if (!iso) {
    return '—';
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const formatRelative = (iso?: string | null, now: Date = new Date()): string => {
  if (!iso) {
    return '—';
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 0) {
    const ahead = Math.abs(diffSec);

    if (ahead < 60) {
      return `in ${ahead}s`;
    }

    if (ahead < 3600) {
      return `in ${Math.round(ahead / 60)}m`;
    }

    if (ahead < 86400) {
      return `in ${Math.round(ahead / 3600)}h`;
    }

    return `in ${Math.round(ahead / 86400)}d`;
  }

  if (diffSec < 60) {
    return `${diffSec}s ago`;
  }

  if (diffSec < 3600) {
    return `${Math.round(diffSec / 60)}m ago`;
  }

  if (diffSec < 86400) {
    return `${Math.round(diffSec / 3600)}h ago`;
  }

  return `${Math.round(diffSec / 86400)}d ago`;
};

/**
 * Browser-locale short tz abbreviation (e.g. "GMT+5:30", "EST"). Used by
 * tables that display timestamps so users can see which zone the values
 * are in without needing a full timestamp + tz string per row.
 */
export const localTimeZoneAbbrev = ((): string => {
  const formatted = new Date()
    .toLocaleTimeString(undefined, { timeZoneName: 'short' });
  const parts = formatted.split(' ');

  return parts[parts.length - 1];
})();
